import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { downloadContratoDocx, slugContratoFilename } from '@/lib/contratoDocxExport';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';

const formatMoney = (v) =>
  v != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v)) : '-';
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

/** Exibe MB de uso de storage (evita “0 MB” quando há consumo pequeno). */
function formatStorageMb(mb) {
  const n = Number(mb);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 0.1) return n.toFixed(2);
  if (n < 10) return n.toFixed(1);
  return String(Math.round(n));
}

function configuracaoEmpresaToForm(raw) {
  const c = raw || {};
  return {
    endereco_completo: c.enderecoCompleto ?? c.endereco_completo ?? '',
    resp_fin_nome: c.respFinNome ?? c.resp_fin_nome ?? '',
    resp_fin_telefone: c.respFinTelefone ?? c.resp_fin_telefone ?? '',
    resp_fin_email: c.respFinEmail ?? c.resp_fin_email ?? '',
    resp_contrato_nome: c.respContratoNome ?? c.resp_contrato_nome ?? '',
    resp_contrato_endereco: c.respContratoEndereco ?? c.resp_contrato_endereco ?? '',
    resp_contrato_cpf: c.respContratoCpf ?? c.resp_contrato_cpf ?? '',
    resp_contrato_rg: c.respContratoRg ?? c.resp_contrato_rg ?? '',
    resp_contrato_telefone: c.respContratoTelefone ?? c.resp_contrato_telefone ?? '',
    resp_contrato_email: c.respContratoEmail ?? c.resp_contrato_email ?? '',
  };
}

export default function AdminEmpresaDetalhe() {
  const { id } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [uso, setUso] = useState(null);
  const [faturas, setFaturas] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState('dados');
  const [carregandoAtividades, setCarregandoAtividades] = useState(false);
  const [acaoExtra, setAcaoExtra] = useState(null); // { tipo, valor? }
  const [editandoDados, setEditandoDados] = useState(false);
  const [formDados, setFormDados] = useState({});
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [modalUsuario, setModalUsuario] = useState(false);
  const [formUsuario, setFormUsuario] = useState({ nome: '', email: '', senha: '', perfil: 'operador', enviar_email: false });
  const [salvandoUsuario, setSalvandoUsuario] = useState(false);
  const [enviandoDadosAcessoId, setEnviandoDadosAcessoId] = useState(null);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ nome: '', email: '', cargo: '', departamento: '', perfil: 'operador' });
  const [newPassword, setNewPassword] = useState('');
  const [modalFatura, setModalFatura] = useState(false);
  const [formFatura, setFormFatura] = useState({
    tipo: 'plano',
    plano_id: '',
    ciclo: '',
    valor: '',
    descricao: '',
    data_vencimento: '',
  });
  const [salvandoFatura, setSalvandoFatura] = useState(false);
  const [integracaoAsaas, setIntegracaoAsaas] = useState(null);
  const [templatesContrato, setTemplatesContrato] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [contratoConteudo, setContratoConteudo] = useState('');
  const [gerandoContrato, setGerandoContrato] = useState(false);
  const [modalNovoTemplate, setModalNovoTemplate] = useState(false);
  const [novoTemplateNome, setNovoTemplateNome] = useState('');
  const [novoTemplateConteudo, setNovoTemplateConteudo] = useState('');
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);
  const [configuracao, setConfiguracao] = useState(null);
  const [formConfiguracao, setFormConfiguracao] = useState({});
  const [baixandoDocx, setBaixandoDocx] = useState(false);
  const [contratoRascunhoAt, setContratoRascunhoAt] = useState(null);
  const [salvandoContratoRascunho, setSalvandoContratoRascunho] = useState(false);
  const [progressoGeracaoContrato, setProgressoGeracaoContrato] = useState(0);

  const carregar = async () => {
    if (!id) return;
    setLoading(true);
    setErro('');
    try {
      const res = await apiAdmin.get('/api/admin/empresas/' + id);
      setEmpresa(res.empresa);
      setUso(res.uso);
      setFaturas(res.faturas || []);
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(err?.data?.error || err?.message || 'Erro ao carregar empresa');
    } finally {
      setLoading(false);
    }
  };

  const carregarPlanos = async () => {
    try {
      const res = await apiAdmin.get('/api/admin/planos');
      setPlanos(Array.isArray(res) ? res : []);
    } catch {
      setPlanos([]);
    }
  };

  const carregarAtividades = async () => {
    setCarregandoAtividades(true);
    try {
      const res = await apiAdmin.get('/api/admin/empresas/' + id + '/atividades');
      setAtividades(Array.isArray(res) ? res : []);
    } catch (_) {
      setAtividades([]);
    } finally {
      setCarregandoAtividades(false);
    }
  };

  useEffect(() => {
    carregar();
    carregarPlanos();
    apiAdmin.get('/api/admin/integracao-asaas').then(setIntegracaoAsaas).catch(() => setIntegracaoAsaas(null));
  }, [id]);

  useEffect(() => {
    if (aba === 'atividades' && id) carregarAtividades();
  }, [aba, id]);

  useEffect(() => {
    if (aba !== 'contrato' || !id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await apiAdmin.get('/api/admin/empresas/' + id + '/templates-contrato');
        if (cancelled) return;
        const templates = Array.isArray(list) ? list : [];
        setTemplatesContrato(templates);

        const rascunho = await apiAdmin.get('/api/admin/empresas/' + id + '/contrato-rascunho').catch(() => null);
        if (cancelled) return;

        if (templates.length > 0) {
          const tidFromRascunho =
            rascunho?.template_id && templates.some((t) => t.id === rascunho.template_id)
              ? rascunho.template_id
              : templates[0].id;
          setSelectedTemplateId(tidFromRascunho);
        }

        if (rascunho && typeof rascunho.conteudo === 'string') {
          setContratoConteudo(rascunho.conteudo);
          setContratoRascunhoAt(rascunho.updated_at || null);
        } else {
          setContratoConteudo('');
          setContratoRascunhoAt(null);
        }
      } catch {
        if (!cancelled) {
          setTemplatesContrato([]);
          setContratoConteudo('');
          setContratoRascunhoAt(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aba, id]);

  useEffect(() => {
    if (!gerandoContrato) {
      setProgressoGeracaoContrato(0);
      return undefined;
    }
    setProgressoGeracaoContrato(12);
    const t = setInterval(() => {
      setProgressoGeracaoContrato((p) => (p >= 90 ? 25 : p + 7));
    }, 450);
    return () => clearInterval(t);
  }, [gerandoContrato]);

  useEffect(() => {
    if (!id) return;
    apiAdmin
      .get('/api/admin/empresas/' + id + '/configuracao')
      .then((arr) => {
        const c = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
        setConfiguracao(c);
        setFormConfiguracao(configuracaoEmpresaToForm(c));
      })
      .catch(() => {
        setConfiguracao(null);
        setFormConfiguracao(configuracaoEmpresaToForm(null));
      });
  }, [id]);

  const gerarContrato = async () => {
    const tid = selectedTemplateId || templatesContrato[0]?.id;
    if (!tid || !id) return;
    setGerandoContrato(true);
    try {
      const res = await apiAdmin.post('/api/admin/empresas/' + id + '/templates-contrato/gerar', { template_id: tid });
      setContratoConteudo(res.conteudo || '');
      setProgressoGeracaoContrato(100);
      const num = res.numero_contrato || res.numeroContrato;
      toast.success(
        num ? `Contrato gerado: Nº ${num}` : 'Contrato gerado com sucesso!'
      );
    } catch (err) {
      const code = err?.data?.code;
      if (code === 'ia_disabled' || err?.status === 503) {
        toast.error(
          err?.data?.error ||
            'IA não configurada. Configure em Super Admin > Config. IA Editais.'
        );
      } else {
        toast.error(getDisplayMessage(err, 'Erro ao gerar contrato.'));
      }
    } finally {
      setGerandoContrato(false);
    }
  };

  const reGerarContrato = async () => {
    if (contratoConteudo.trim()) {
      const ok = window.confirm(
        'Substituir o texto atual por um novo contrato gerado pela IA? (O texto não salvo será perdido.)'
      );
      if (!ok) return;
    }
    await gerarContrato();
  };

  const salvarContratoRascunho = async () => {
    if (!id) return;
    const tid = selectedTemplateId || templatesContrato[0]?.id || null;
    setSalvandoContratoRascunho(true);
    try {
      const res = await apiAdmin.put('/api/admin/empresas/' + id + '/contrato-rascunho', {
        conteudo: contratoConteudo,
        template_id: tid || undefined,
      });
      setContratoRascunhoAt(res.updated_at || null);
      toast.success('Contrato salvo.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao salvar contrato.'));
    } finally {
      setSalvandoContratoRascunho(false);
    }
  };

  const handleDownloadContratoDocx = async () => {
    if (!contratoConteudo?.trim() || !id) return;
    setBaixandoDocx(true);
    try {
      const name = slugContratoFilename(empresa?.razaoSocial, id);
      await downloadContratoDocx(contratoConteudo, name);
    } catch {
      toast.error('Não foi possível gerar o arquivo .DOCX.');
    } finally {
      setBaixandoDocx(false);
    }
  };

  const criarTemplate = async () => {
    if (!id || !novoTemplateNome.trim()) {
      toast.error('Informe o nome do template.');
      return;
    }
    setSalvandoTemplate(true);
    try {
      await apiAdmin.post('/api/admin/empresas/' + id + '/templates-contrato', {
        nome: novoTemplateNome.trim(),
        categoria: 'tecnologia',
        conteudo: novoTemplateConteudo,
      });
      const list = await apiAdmin.get('/api/admin/empresas/' + id + '/templates-contrato');
      setTemplatesContrato(Array.isArray(list) ? list : []);
      setModalNovoTemplate(false);
      setNovoTemplateNome('');
      setNovoTemplateConteudo('');
      toast.success('Template criado com sucesso!');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao criar template.'));
    } finally {
      setSalvandoTemplate(false);
    }
  };

  const iniciarEdicaoDados = () => {
    const e = empresa || {};
    setFormDados({
      razao_social: e.razaoSocial || '',
      nome_fantasia: e.nomeFantasia || '',
      cnpj: e.cnpj || '',
      email_principal: e.emailPrincipal || '',
      telefone: e.telefone || '',
      plano_id: e.plano?.id || e.planoId || '',
      limite_usuarios: e.limiteUsuarios,
      limite_editais: e.limiteEditais ?? '',
      limite_propostas: e.limitePropostas ?? '',
      data_adesao: e.dataAtivacao ? e.dataAtivacao.slice(0, 10) : '',
      data_expiracao: e.dataExpiracao ? e.dataExpiracao.slice(0, 10) : '',
    });
    setFormConfiguracao(configuracaoEmpresaToForm(configuracao));
    setEditandoDados(true);
  };

  const salvarDados = async () => {
    const cnpjDigits = String(formDados.cnpj || '').replace(/\D/g, '');
    if (cnpjDigits.length !== 14) {
      toast.error('Informe um CNPJ válido (14 dígitos).');
      return;
    }
    setSalvandoDados(true);
    try {
      const { data_adesao, ...rest } = formDados;
      const payload = {
        ...rest,
        cnpj: cnpjDigits,
        plano_id: formDados.plano_id || null,
        data_ativacao: data_adesao || null,
        limite_usuarios: formDados.limite_usuarios != null && formDados.limite_usuarios !== '' ? Number(formDados.limite_usuarios) : undefined,
        limite_editais: formDados.limite_editais != null && formDados.limite_editais !== '' ? Number(formDados.limite_editais) : undefined,
        limite_propostas: formDados.limite_propostas != null && formDados.limite_propostas !== '' ? Number(formDados.limite_propostas) : undefined,
      };
      await apiAdmin.put('/api/admin/empresas/' + id, payload);
      try {
        await apiAdmin.put('/api/admin/empresas/' + id + '/configuracao', formConfiguracao);
        const resCfg = await apiAdmin.get('/api/admin/empresas/' + id + '/configuracao');
        const c = Array.isArray(resCfg) && resCfg.length > 0 ? resCfg[0] : null;
        setConfiguracao(c);
        setFormConfiguracao(configuracaoEmpresaToForm(c));
      } catch (cfgErr) {
        toast.error(getDisplayMessage(cfgErr, 'Empresa salva, mas falhou ao salvar endereço/responsáveis.'));
      }
      setEditandoDados(false);
      carregar();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao salvar dados.'));
    } finally {
      setSalvandoDados(false);
    }
  };

  const liberarCadastro = async () => {
    if (!id) return;
    try {
      await apiAdmin.post('/api/admin/empresas/' + id + '/liberar-cadastro');
      toast.success('Cadastro liberado. Trial de 14 dias iniciado; e-mail enviado ao administrador.');
      carregar();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao liberar cadastro.'));
    }
  };

  const abrirNovoUsuario = () => {
    setFormUsuario({ nome: '', email: '', senha: '', perfil: 'operador', enviar_email: false });
    setModalUsuario(true);
  };

  const salvarUsuario = async () => {
    if (!formUsuario.nome?.trim() || !formUsuario.email?.trim() || !formUsuario.senha) {
      toast.error('Preencha nome, e-mail e senha.');
      return;
    }
    setSalvandoUsuario(true);
    try {
      const payload = { ...formUsuario };
      if (formUsuario.enviar_email) payload.enviar_email = true;
      await apiAdmin.post('/api/admin/empresas/' + id + '/usuarios', payload);
      setModalUsuario(false);
      carregar();
      const nome = formUsuario.nome.trim();
      const email = formUsuario.email.trim();
      if (formUsuario.enviar_email) {
        toast.success('Usuário criado com sucesso!', {
          description: `Os dados de acesso (link e senha temporária) foram enviados para ${email}. Peça ao usuário para verificar a caixa de entrada e alterar a senha no primeiro acesso.`,
          duration: 6000,
        });
      } else {
        toast.success('Usuário criado com sucesso!', {
          description: `${nome} (${email}) já pode ser convidado. Use o ícone de chave na lista para "Enviar dados de acesso" e o e-mail será enviado com a senha temporária.`,
          duration: 6000,
        });
      }
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao criar usuário.'));
    } finally {
      setSalvandoUsuario(false);
    }
  };

  const enviarDadosAcesso = async (usuario) => {
    setEnviandoDadosAcessoId(usuario.id);
    try {
      const res = await apiAdmin.post(`/api/admin/empresas/${id}/usuarios/${usuario.id}/enviar-dados-acesso`);
      if (res.smtp_nao_configurado) {
        toast.warning('E-mail não enviado', {
          description: 'A senha temporária foi gerada, mas o SMTP não está configurado. Configure em Admin > Config. SMTP. Enquanto isso, verifique os logs do servidor para obter a senha temporária e repassar ao usuário.',
          duration: 8000,
        });
      } else {
        toast.success('E-mail enviado com sucesso!', {
          description: `${usuario.nome || usuario.email} receberá em ${usuario.email} o link de acesso e a senha temporária. Recomende a alteração da senha no primeiro acesso.`,
          duration: 6000,
        });
      }
      carregar();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao enviar dados de acesso.'));
    } finally {
      setEnviandoDadosAcessoId(null);
    }
  };

  const abrirNovaFatura = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    setFormFatura({
      tipo: 'plano',
      plano_id: planos[0]?.id || '',
      ciclo: 'mensal',
      valor: '',
      descricao: '',
      data_vencimento: d.toISOString().slice(0, 10),
    });
    setModalFatura(true);
  };

  const valorCalculadoFatura = () => {
    if (formFatura.tipo !== 'plano' || !formFatura.plano_id || !formFatura.ciclo) return null;
    const plano = planos.find((p) => p.id === formFatura.plano_id);
    if (!plano) return null;
    const precoMensal = Number(plano.precoMensal);
    const precoAnual = plano.precoAnual != null ? Number(plano.precoAnual) : precoMensal * 12;
    switch (formFatura.ciclo) {
      case 'mensal': return precoMensal;
      case 'trimestral': return precoMensal * 3;
      case 'semestral': return precoMensal * 6;
      case 'anual': return precoAnual;
      default: return precoMensal;
    }
  };

  const salvarFatura = async () => {
    if (!formFatura.data_vencimento) {
      alert('Informe a data de vencimento.');
      return;
    }
    if (formFatura.tipo === 'plano') {
      if (!formFatura.plano_id || !formFatura.ciclo) {
        alert('Selecione o plano e o ciclo de cobrança.');
        return;
      }
    } else {
      if (!formFatura.valor || Number(formFatura.valor) <= 0) {
        alert('Informe o valor.');
        return;
      }
    }
    setSalvandoFatura(true);
    try {
      const payload = { data_vencimento: formFatura.data_vencimento };
      if (formFatura.tipo === 'plano') {
        payload.plano_id = formFatura.plano_id;
        payload.ciclo = formFatura.ciclo;
      } else {
        payload.valor = Number(formFatura.valor);
        payload.descricao = formFatura.descricao || undefined;
      }
      const data = await apiAdmin.post('/api/admin/empresas/' + id + '/criar-fatura', payload);
      setModalFatura(false);
      carregar();
      if (data?.integracaoAsaas?.cobrancaRegistradaNoAsaas) {
        toast.success('Fatura criada e cobrança gerada no ASAAS.');
      } else {
        toast.success('Fatura registrada.');
        const msgs = data?.integracaoAsaas?.mensagens?.filter(Boolean) ?? [];
        if (msgs.length) {
          toast.warning(msgs.join(' '), { duration: 10000 });
        } else {
          toast.warning('Cobrança não foi criada no ASAAS. Verifique Config. ASAAS e o painel do gateway.', {
            duration: 10000,
          });
        }
      }
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro na operação.'));
    } finally {
      setSalvandoFatura(false);
    }
  };

  const executarAcao = async () => {
    const { tipo, valor } = acaoExtra || {};
    try {
      if (tipo === 'ativar') {
        await apiAdmin.post('/api/admin/empresas/' + id + '/ativar');
      } else if (tipo === 'suspender') {
        await apiAdmin.post('/api/admin/empresas/' + id + '/suspender', { motivo: 'Suspensão administrativa' });
      } else if (tipo === 'alterarPlano' && valor) {
        await apiAdmin.post('/api/admin/empresas/' + id + '/alterar-plano', { plano_id: valor });
      } else if (tipo === 'estenderTrial' && valor) {
        await apiAdmin.post('/api/admin/empresas/' + id + '/estender-trial', { dias: Number(valor) });
      } else if (tipo === 'resetarSenha' && valor) {
        await apiAdmin.post('/api/admin/empresas/' + id + '/resetar-senha-admin', { nova_senha: valor });
      }
      setAcaoExtra(null);
      carregar();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro na operação.'));
    }
  };

  if (loading && !empresa) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
      </div>
    );
  }

  if (erro && !empresa) {
    return (
      <div className="p-6">
        <p className="text-red-600 mb-2">{erro}</p>
        <Link to="/admin/empresas" className="text-slate-600 hover:underline">Voltar às empresas</Link>
      </div>
    );
  }

  const e = empresa || {};
  const usuarios = e.usuarios || [];

  const abas = [
    { key: 'dados', label: 'Dados' },
    { key: 'usuarios', label: 'Usuários' },
    { key: 'uso', label: 'Uso' },
    { key: 'configuracao', label: 'Configuração' },
    { key: 'contrato', label: 'Contrato' },
    { key: 'faturas', label: 'Faturas' },
    { key: 'atividades', label: 'Atividades' },
    { key: 'acoes', label: 'Ações' },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <Link to="/admin/empresas" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Voltar às empresas
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">{e.razaoSocial || e.nomeFantasia || 'Empresa'}</h1>
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            e.status === 'pending'
              ? 'bg-violet-100 text-violet-800'
              : e.status === 'active'
                ? 'bg-green-100 text-green-800'
                : e.status === 'suspended'
                  ? 'bg-red-100 text-red-800'
                  : e.status === 'trial'
                    ? 'bg-amber-100 text-amber-800'
                    : e.status === 'awaiting_payment'
                      ? 'bg-orange-100 text-orange-900'
                      : 'bg-slate-100 text-slate-800'
          }`}
        >
          {e.status === 'pending'
            ? 'Aguardando liberação'
            : e.status === 'awaiting_payment'
              ? 'Aguardando pagamento'
              : e.status}
        </span>
        {e.plano && <span className="text-sm text-slate-500">{e.plano.nome}</span>}
        {e.status === 'pending' && (
          <button
            type="button"
            onClick={liberarCadastro}
            className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Liberar acesso (trial 14 dias)
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
        {abas.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => setAba(a.key)}
            className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap ${
              aba === a.key ? 'bg-white border border-slate-200 border-b-white -mb-px' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        {aba === 'dados' && (
          <div>
            {!editandoDados ? (
              <>
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={iniciarEdicaoDados}
                    className="px-3 py-1.5 bg-slate-700 text-white rounded text-sm hover:bg-slate-600"
                  >
                    Editar dados
                  </button>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-slate-500">Razão social</dt><dd className="font-medium">{e.razaoSocial}</dd></div>
                  <div><dt className="text-slate-500">Nome fantasia</dt><dd className="font-medium">{e.nomeFantasia || '-'}</dd></div>
                  <div className="sm:col-span-2">
                    <dt className="text-slate-500">Endereço da sede</dt>
                    <dd className="font-medium whitespace-pre-wrap">{configuracao?.enderecoCompleto || '-'}</dd>
                  </div>
                  <div><dt className="text-slate-500">CNPJ</dt><dd className="font-medium">{e.cnpj}</dd></div>
                  <div><dt className="text-slate-500">E-mail</dt><dd className="font-medium">{e.emailPrincipal}</dd></div>
                  <div><dt className="text-slate-500">Telefone</dt><dd className="font-medium">{e.telefone || '-'}</dd></div>
                  <div><dt className="text-slate-500">Plano</dt><dd className="font-medium">{e.plano?.nome ?? '-'}</dd></div>
                  <div><dt className="text-slate-500">Data de Adesão</dt><dd className="font-medium">{formatDate(e.dataAtivacao || e.createdAt)}</dd></div>
                  <div><dt className="text-slate-500">Data expiração</dt><dd className="font-medium">{formatDate(e.dataExpiracao)}</dd></div>
                  <div><dt className="text-slate-500">Limite usuários</dt><dd className="font-medium">{e.limiteUsuarios}</dd></div>
                  <div><dt className="text-slate-500">Limite editais</dt><dd className="font-medium">{e.limiteEditais ?? 'Ilimitado'}</dd></div>
                  <div><dt className="text-slate-500">Limite propostas</dt><dd className="font-medium">{e.limitePropostas ?? 'Ilimitado'}</dd></div>
                </dl>
                <div className="mt-6 space-y-4 border-t border-slate-100 pt-6">
                  <h4 className="font-semibold text-slate-800 text-sm">Responsável financeiro</h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="sm:col-span-2">
                      <dt className="text-slate-500">Nome completo</dt>
                      <dd className="font-medium">{configuracao?.respFinNome || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Telefone</dt>
                      <dd className="font-medium">{configuracao?.respFinTelefone || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">E-mail</dt>
                      <dd className="font-medium">{configuracao?.respFinEmail || '-'}</dd>
                    </div>
                  </dl>
                  <h4 className="font-semibold text-slate-800 text-sm pt-2">Responsável contrato</h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="sm:col-span-2">
                      <dt className="text-slate-500">Nome completo</dt>
                      <dd className="font-medium">{configuracao?.respContratoNome || '-'}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-slate-500">Endereço</dt>
                      <dd className="font-medium whitespace-pre-wrap">{configuracao?.respContratoEndereco || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">CPF</dt>
                      <dd className="font-medium">{configuracao?.respContratoCpf || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">RG</dt>
                      <dd className="font-medium">{configuracao?.respContratoRg || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Telefone</dt>
                      <dd className="font-medium">{configuracao?.respContratoTelefone || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">E-mail</dt>
                      <dd className="font-medium">{configuracao?.respContratoEmail || '-'}</dd>
                    </div>
                  </dl>
                </div>
              </>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Razão social</label>
                    <input value={formDados.razao_social || ''} onChange={(ev) => setFormDados((f) => ({ ...f, razao_social: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Nome fantasia</label>
                    <input value={formDados.nome_fantasia || ''} onChange={(ev) => setFormDados((f) => ({ ...f, nome_fantasia: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-600 mb-1">Endereço da sede (razão social)</label>
                    <Textarea
                      rows={3}
                      value={formConfiguracao.endereco_completo || ''}
                      onChange={(ev) => setFormConfiguracao((f) => ({ ...f, endereco_completo: ev.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Logradouro, número, bairro, cidade/UF, CEP"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">CNPJ</label>
                    <input
                      inputMode="numeric"
                      autoComplete="off"
                      value={formDados.cnpj || ''}
                      onChange={(ev) => setFormDados((f) => ({ ...f, cnpj: ev.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Somente números"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">E-mail principal</label>
                    <input type="email" value={formDados.email_principal || ''} onChange={(ev) => setFormDados((f) => ({ ...f, email_principal: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Telefone</label>
                    <input value={formDados.telefone || ''} onChange={(ev) => setFormDados((f) => ({ ...f, telefone: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Plano</label>
                    <select
                      value={formDados.plano_id || ''}
                      onChange={(ev) => setFormDados((f) => ({ ...f, plano_id: ev.target.value }))}
                      className="w-full px-3 py-2 border rounded bg-white"
                    >
                      <option value="">— Nenhum —</option>
                      {planos.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Data de Adesão</label>
                    <input
                      type="date"
                      value={formDados.data_adesao || ''}
                      onChange={(ev) => setFormDados((f) => ({ ...f, data_adesao: ev.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Data expiração</label>
                    <input type="date" value={formDados.data_expiracao || ''} onChange={(ev) => setFormDados((f) => ({ ...f, data_expiracao: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-6 space-y-4">
                  <h4 className="font-semibold text-slate-800">Responsável financeiro</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-slate-600 mb-1">Nome completo</label>
                      <input
                        value={formConfiguracao.resp_fin_nome || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_fin_nome: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Nome do responsável financeiro"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">Telefone</label>
                      <input
                        value={formConfiguracao.resp_fin_telefone || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_fin_telefone: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="(XX) XXXXX-XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">E-mail</label>
                      <input
                        type="email"
                        value={formConfiguracao.resp_fin_email || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_fin_email: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="email@empresa.com.br"
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-6 space-y-4">
                  <h4 className="font-semibold text-slate-800">Responsável contrato</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-slate-600 mb-1">Nome completo</label>
                      <input
                        value={formConfiguracao.resp_contrato_nome || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_nome: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Nome do responsável pelo contrato"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-slate-600 mb-1">Endereço</label>
                      <input
                        value={formConfiguracao.resp_contrato_endereco || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_endereco: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Endereço completo"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">CPF</label>
                      <input
                        value={formConfiguracao.resp_contrato_cpf || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_cpf: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="000.000.000-00"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">RG</label>
                      <input
                        value={formConfiguracao.resp_contrato_rg || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_rg: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="RG"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">Telefone</label>
                      <input
                        value={formConfiguracao.resp_contrato_telefone || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_telefone: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="(XX) XXXXX-XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 mb-1">E-mail</label>
                      <input
                        type="email"
                        value={formConfiguracao.resp_contrato_email || ''}
                        onChange={(ev) => setFormConfiguracao((f) => ({ ...f, resp_contrato_email: ev.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="email@empresa.com.br"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-slate-600 mb-1">Limite usuários</label>
                    <input type="number" min="1" value={formDados.limite_usuarios ?? ''} onChange={(ev) => setFormDados((f) => ({ ...f, limite_usuarios: ev.target.value }))} className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Limite editais</label>
                    <input type="number" value={formDados.limite_editais ?? ''} onChange={(ev) => setFormDados((f) => ({ ...f, limite_editais: ev.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="Vazio = ilimitado" />
                  </div>
                  <div>
                    <label className="block text-slate-600 mb-1">Limite propostas</label>
                    <input type="number" value={formDados.limite_propostas ?? ''} onChange={(ev) => setFormDados((f) => ({ ...f, limite_propostas: ev.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="Vazio = ilimitado" />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Ao alterar o plano, os limites de usuários, editais, propostas e armazenamento são ajustados conforme o plano escolhido. O botão Salvar grava também endereço da sede, responsáveis financeiro e contrato.
                </p>
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={salvarDados} disabled={salvandoDados} className="px-4 py-2 bg-slate-800 text-white rounded text-sm disabled:opacity-50">Salvar</button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditandoDados(false);
                      setFormConfiguracao(configuracaoEmpresaToForm(configuracao));
                    }}
                    className="px-4 py-2 border rounded text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {aba === 'usuarios' && (
          <div>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={abrirNovoUsuario}
                className="px-3 py-1.5 bg-slate-700 text-white rounded text-sm hover:bg-slate-600"
              >
                + Adicionar usuário
              </button>
            </div>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 text-slate-600">Nome</th>
                  <th className="text-left py-2 text-slate-600">E-mail</th>
                  <th className="text-left py-2 text-slate-600">Perfil</th>
                  <th className="text-left py-2 text-slate-600">Status</th>
                  <th className="text-left py-2 text-slate-600">Último login</th>
                  <th className="text-right py-2 text-slate-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usuarios.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="py-2">
                      <div>
                        <p className="font-medium">{u.nome}</p>
                        {(u.cargo || u.departamento) && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {u.cargo || ""} {u.cargo && u.departamento ? "•" : ""} {u.departamento || ""}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="py-2">{u.email}</td>
                    <td className="py-2">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs capitalize">{u.perfil}</span>
                    </td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        u.ativo === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {u.ativo === false ? 'Desativado' : 'Ativo'}
                      </span>
                    </td>
                    <td className="py-2">{u.ultimoLogin ? formatDate(u.ultimoLogin) : '-'}</td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setEditUserData({
                              nome: u.nome || '',
                              email: u.email || '',
                              cargo: u.cargo || '',
                              departamento: u.departamento || '',
                              perfil: u.perfil || 'operador'
                            });
                            setEditUserDialogOpen(true);
                          }}
                          className="px-2 py-1 text-slate-600 hover:bg-slate-200 rounded text-xs"
                          title="Editar usuário"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setResetPasswordDialogOpen(true);
                            setNewPassword('');
                          }}
                          className="px-2 py-1 text-slate-600 hover:bg-slate-200 rounded text-xs"
                          title="Resetar senha"
                        >
                          🔑
                        </button>
                        <button
                          type="button"
                          onClick={() => enviarDadosAcesso(u)}
                          disabled={enviandoDadosAcessoId === u.id}
                          className="px-2 py-1 text-slate-600 hover:bg-slate-200 rounded text-xs disabled:opacity-50"
                          title="Enviar dados de acesso por e-mail"
                        >
                          {enviandoDadosAcessoId === u.id ? '...' : '📧'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setDeactivateDialogOpen(true);
                          }}
                          className={`px-2 py-1 rounded text-xs ${
                            u.ativo === false 
                              ? 'text-green-600 hover:bg-green-50' 
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={u.ativo === false ? "Ativar usuário" : "Desativar usuário"}
                        >
                          {u.ativo === false ? '✓' : '✕'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {modalUsuario && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Adicionar usuário</h3>
                  <div className="space-y-2">
                    <input value={formUsuario.nome} onChange={(e) => setFormUsuario((f) => ({ ...f, nome: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="Nome" />
                    <input type="email" value={formUsuario.email} onChange={(e) => setFormUsuario((f) => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="E-mail" />
                    <input type="password" value={formUsuario.senha} onChange={(e) => setFormUsuario((f) => ({ ...f, senha: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="Senha" />
                    <select value={formUsuario.perfil} onChange={(e) => setFormUsuario((f) => ({ ...f, perfil: e.target.value }))} className="w-full px-3 py-2 border rounded">
                      <option value="admin">Admin</option>
                      <option value="operador">Operador</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={formUsuario.enviar_email || false}
                        onChange={(e) => setFormUsuario((f) => ({ ...f, enviar_email: e.target.checked }))}
                        className="rounded"
                      />
                      Enviar dados de acesso por e-mail
                    </label>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button type="button" onClick={() => setModalUsuario(false)} className="px-4 py-2 border rounded text-sm">Cancelar</button>
                    <button type="button" onClick={salvarUsuario} disabled={salvandoUsuario} className="px-4 py-2 bg-slate-800 text-white rounded text-sm disabled:opacity-50">Incluir</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dialog para editar usuário */}
        {editUserDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Editar usuário</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-slate-600 text-sm mb-1">Nome *</label>
                  <input
                    value={editUserData.nome}
                    onChange={(e) => setEditUserData({ ...editUserData, nome: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 text-sm mb-1">E-mail</label>
                  <input
                    type="email"
                    value={editUserData.email}
                    disabled
                    className="w-full px-3 py-2 border rounded bg-slate-50 text-slate-600"
                    placeholder="E-mail (não pode ser alterado)"
                  />
                  <p className="text-xs text-slate-500 mt-1">O e-mail não pode ser alterado</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-slate-600 text-sm mb-1">Cargo</label>
                    <input
                      value={editUserData.cargo}
                      onChange={(e) => setEditUserData({ ...editUserData, cargo: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Ex: Gerente"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 text-sm mb-1">Departamento</label>
                    <input
                      value={editUserData.departamento}
                      onChange={(e) => setEditUserData({ ...editUserData, departamento: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="Ex: Licitações"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-600 text-sm mb-1">Perfil</label>
                  <select
                    value={editUserData.perfil}
                    onChange={(e) => setEditUserData({ ...editUserData, perfil: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="operador">Operador</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditUserDialogOpen(false);
                    setSelectedUser(null);
                    setEditUserData({ nome: '', email: '', cargo: '', departamento: '', perfil: 'operador' });
                  }}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!editUserData.nome) {
                      alert('Nome é obrigatório');
                      return;
                    }
                    try {
                      await apiAdmin.put(`/api/admin/empresas/${id}/usuarios/${selectedUser.id}`, {
                        nome: editUserData.nome,
                        perfil: editUserData.perfil,
                        cargo: editUserData.cargo || null,
                        departamento: editUserData.departamento || null,
                      });
                      setEditUserDialogOpen(false);
                      setSelectedUser(null);
                      setEditUserData({ nome: '', email: '', cargo: '', departamento: '', perfil: 'operador' });
                      carregar();
                    } catch (err) {
                      toast.error(getDisplayMessage(err, 'Erro ao atualizar usuário.'));
                    }
                  }}
                  className="px-4 py-2 bg-slate-800 text-white rounded text-sm"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog para resetar senha */}
        {resetPasswordDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Resetar senha</h3>
              <p className="text-sm text-slate-600 mb-4">
                Defina uma nova senha para {selectedUser?.nome || selectedUser?.email}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-slate-600 text-sm mb-1">Nova senha *</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Digite a nova senha (mínimo 6 caracteres)"
                    minLength={6}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setResetPasswordDialogOpen(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!newPassword || newPassword.length < 6) {
                      alert('A senha deve ter pelo menos 6 caracteres');
                      return;
                    }
                    try {
                      await apiAdmin.put(`/api/admin/empresas/${id}/usuarios/${selectedUser.id}`, {
                        senha: newPassword,
                      });
                      setResetPasswordDialogOpen(false);
                      setSelectedUser(null);
                      setNewPassword('');
                      alert('Senha resetada com sucesso!');
                      carregar();
                    } catch (err) {
                      toast.error(getDisplayMessage(err, 'Erro ao resetar senha.'));
                    }
                  }}
                  disabled={!newPassword || newPassword.length < 6}
                  className="px-4 py-2 bg-slate-800 text-white rounded text-sm disabled:opacity-50"
                >
                  Resetar senha
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog para desativar/ativar usuário */}
        {deactivateDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="font-semibold text-slate-800 mb-4">
                {selectedUser?.ativo === false ? "Ativar Usuário" : "Desativar Usuário"}
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                {selectedUser?.ativo === false ? (
                  <>
                    Tem certeza que deseja ativar o usuário <strong>{selectedUser?.nome || selectedUser?.email}</strong>?
                    <br /><br />
                    O usuário poderá acessar o sistema novamente.
                  </>
                ) : (
                  <>
                    Tem certeza que deseja desativar o usuário <strong>{selectedUser?.nome || selectedUser?.email}</strong>?
                    <br /><br />
                    <span className="text-amber-600 font-medium">⚠️ Atenção:</span> O usuário não poderá mais acessar o sistema enquanto estiver desativado.
                  </>
                )}
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setDeactivateDialogOpen(false);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await apiAdmin.put(`/api/admin/empresas/${id}/usuarios/${selectedUser.id}`, {
                        ativo: !selectedUser.ativo,
                      });
                      setDeactivateDialogOpen(false);
                      setSelectedUser(null);
                      alert(selectedUser.ativo === false ? 'Usuário ativado com sucesso!' : 'Usuário desativado com sucesso!');
                      carregar();
                    } catch (err) {
                      toast.error(getDisplayMessage(err, 'Erro ao atualizar status do usuário.'));
                    }
                  }}
                  className={`px-4 py-2 text-white rounded text-sm ${
                    selectedUser?.ativo === false ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {selectedUser?.ativo === false ? "Ativar" : "Desativar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {aba === 'configuracao' && (
          <div className="text-sm text-slate-600 py-4">
            <p className="mb-2">
              Os dados de endereço da sede, responsável financeiro e responsável contrato foram movidos para a aba{' '}
              <span className="font-medium text-slate-800">Dados</span> (edição junto ao cadastro da empresa).
            </p>
            <p>Esta aba fica reservada para configurações adicionais no futuro.</p>
          </div>
        )}

        {aba === 'uso' && (
          <div className="space-y-6 text-sm text-slate-700">
            <p className="text-slate-600 leading-relaxed">
              O plano é <span className="font-medium text-slate-800">mensal</span>: parte dos limites{' '}
              <span className="font-medium text-slate-800">renova a cada ciclo</span> (alinhado ao período de fatura —
              contadores de editais e propostas zeram após o vencimento / renovação). Outros limites{' '}
              <span className="font-medium text-slate-800">não zeram</span>: refletem o que está cadastrado na conta
              (usuários ativos e espaço ocupado por arquivos).
            </p>

            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Renovam e zeram após o vencimento da fatura (uso no mês)
              </h3>
              <ul className="space-y-2 list-none">
                <li className="flex flex-wrap justify-between gap-2 border-b border-slate-200/80 pb-2">
                  <span className="text-slate-600">Editais criados (mês)</span>
                  <span className="font-medium tabular-nums">
                    {uso?.editaisCriados ?? 0}
                    {e.limiteEditais != null ? ` / ${e.limiteEditais}` : ''}
                  </span>
                </li>
                <li className="flex flex-wrap justify-between gap-2">
                  <span className="text-slate-600">Propostas criadas (mês)</span>
                  <span className="font-medium tabular-nums">
                    {uso?.propostasCriadas ?? 0}
                    {e.limitePropostas != null ? ` / ${e.limitePropostas}` : ''}
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Não renovam — consideram o cadastro atual
              </h3>
              <ul className="space-y-2 list-none">
                <li className="flex flex-wrap justify-between gap-2 border-b border-slate-200 pb-2">
                  <span className="text-slate-600">Usuários ativos</span>
                  <span className="font-medium tabular-nums">
                    {uso?.usuariosAtivos ?? 0} / {e.limiteUsuarios ?? '—'}
                  </span>
                </li>
                <li className="space-y-1">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="text-slate-600">Uso de storage (uso / limite do plano)</span>
                    <span className="font-medium tabular-nums">
                      {formatStorageMb(uso?.storage_usado_mb)} MB / {e.limiteStorageMb ?? 0} MB
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Soma o tamanho dos anexos em <span className="font-medium text-slate-600">Documento</span> (quando
                    informado) mais os arquivos enviados pela API de upload (<span className="font-medium text-slate-600">UploadBlob</span>
                    / registro de uso). Antes só documentos com campo de tamanho preenchido entravam na conta — por isso o
                    uso podia aparecer zerado mesmo com anexos por link de upload.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        )}

        {aba === 'contrato' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Ao clicar em &quot;Gerar Contrato&quot;, a IA (Config. IA Editais) monta o texto com a empresa cliente como{' '}
              <span className="font-medium">CONTRATANTE</span> (razão social, CNPJ, endereço, responsável financeiro e
              responsável pelo contrato na aba Dados). A prestadora do SaaS é a{' '}
              <span className="font-medium">CONTRATADA</span>, cadastrada em Super Admin &gt; Config. CONTRATADA (razão
              social, CNPJ e endereço); use também o placeholder{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">{'{{prestadora_qualificacao}}'}</code> nos templates.
              Variáveis de ambiente <code className="text-xs bg-slate-100 px-1 rounded">CONTRATO_CONTRATANTE_*</code>{' '}
              (nome legado) podem preencher a prestadora se o cadastro estiver vazio. Outros placeholders:{' '}
              {'{{razao_social}}'}, {'{{cnpj}}'}, {'{{resp_contrato_nome}}'}, etc.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="min-w-[200px]">
                <label className="block text-slate-600 text-sm mb-1">Template</label>
                <select
                  value={selectedTemplateId || templatesContrato[0]?.id || ''}
                  onChange={(ev) => {
                    setSelectedTemplateId(ev.target.value);
                    setContratoConteudo('');
                  }}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Selecione</option>
                  {templatesContrato.map((t) => (
                    <option key={t.id} value={t.id}>{t.nome} {t.categoria ? `(${t.categoria})` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 pt-6">
                <button
                  type="button"
                  onClick={gerarContrato}
                  disabled={gerandoContrato || !(selectedTemplateId || templatesContrato[0]?.id)}
                  className="px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 disabled:opacity-50"
                >
                  {gerandoContrato ? 'Gerando...' : 'Gerar Contrato'}
                </button>
                <button
                  type="button"
                  onClick={reGerarContrato}
                  disabled={gerandoContrato || !(selectedTemplateId || templatesContrato[0]?.id)}
                  className="px-4 py-2 border border-slate-400 rounded text-sm hover:bg-slate-100 disabled:opacity-50"
                >
                  Re-gerar contrato
                </button>
                <button
                  type="button"
                  onClick={salvarContratoRascunho}
                  disabled={salvandoContratoRascunho}
                  className="px-4 py-2 bg-emerald-700 text-white rounded text-sm hover:bg-emerald-600 disabled:opacity-50"
                >
                  {salvandoContratoRascunho ? 'Salvando...' : 'Salvar contrato'}
                </button>
                <button
                  type="button"
                  onClick={() => setModalNovoTemplate(true)}
                  className="px-4 py-2 border rounded text-sm hover:bg-slate-100"
                >
                  + Novo Template
                </button>
              </div>
            </div>
            {gerandoContrato && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 space-y-2">
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slate-600" />
                  </span>
                  Gerando contrato com IA — aguarde (pode levar mais de um minuto).
                </p>
                <Progress
                  value={progressoGeracaoContrato}
                  className="h-2 bg-slate-200"
                  indicatorClassName="bg-slate-700"
                />
              </div>
            )}
            <div>
              <div className="flex flex-wrap items-end justify-between gap-2 mb-1">
                <div>
                  <label className="block text-slate-600 text-sm">Conteúdo do contrato (editável)</label>
                  {contratoRascunhoAt && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Último salvamento:{' '}
                      {new Date(contratoRascunhoAt).toLocaleString('pt-BR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleDownloadContratoDocx}
                  disabled={baixandoDocx || !contratoConteudo.trim()}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {baixandoDocx ? 'Gerando .DOCX...' : 'Baixar .DOCX'}
                </button>
              </div>
              <textarea
                value={contratoConteudo}
                onChange={(e) => setContratoConteudo(e.target.value)}
                placeholder="Clique em 'Gerar Contrato' para preencher com a IA (requer Config. IA Editais)"
                rows={18}
                className="w-full px-3 py-2 border rounded font-mono text-sm"
              />
            </div>
            {modalNovoTemplate && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Novo Template de Contrato</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-slate-600 text-sm mb-1">Nome do template</label>
                      <input value={novoTemplateNome} onChange={(e) => setNovoTemplateNome(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="Ex: Contrato de Consultoria" />
                    </div>
                    <div>
                      <label className="block text-slate-600 text-sm mb-1">Conteúdo</label>
                      <textarea value={novoTemplateConteudo} onChange={(e) => setNovoTemplateConteudo(e.target.value)} placeholder="Use placeholders como {{razao_social}}, {{cnpj}}..." rows={12} className="w-full px-3 py-2 border rounded font-mono text-sm" />
                    </div>
                    <div className="flex gap-2 pt-4">
                      <button type="button" onClick={() => { setModalNovoTemplate(false); setNovoTemplateNome(''); setNovoTemplateConteudo(''); }} className="px-4 py-2 border rounded text-sm">Cancelar</button>
                      <button type="button" onClick={criarTemplate} disabled={salvandoTemplate || !novoTemplateNome.trim()} className="px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 disabled:opacity-50">{salvandoTemplate ? 'Salvando...' : 'Criar Template'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {aba === 'faturas' && (
          <div>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={abrirNovaFatura}
                className="px-3 py-1.5 bg-slate-700 text-white rounded text-sm hover:bg-slate-600"
              >
                + Nova fatura
              </button>
            </div>
            {e.asaasCustomerId ? (
              <p className="text-xs text-green-700 mb-2">
                ✓ Cliente vinculado ao ASAAS — cobranças são geradas automaticamente.
                {integracaoAsaas?.asaasBaseUrl && (
                  <a href={`${integracaoAsaas.asaasBaseUrl}/customers/${e.asaasCustomerId}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">Abrir no ASAAS →</a>
                )}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mb-2">⚠ Empresa sem vínculo ASAAS. Ao criar a fatura, o sistema tentará cadastrar o cliente no ASAAS.</p>
            )}
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 text-slate-600">Número</th>
                  <th className="text-left py-2 text-slate-600">Plano / Ciclo</th>
                  <th className="text-left py-2 text-slate-600">Valor</th>
                  <th className="text-left py-2 text-slate-600">Vencimento</th>
                  <th className="text-left py-2 text-slate-600">Status</th>
                  <th className="text-left py-2 text-slate-600">ASAAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {faturas.map((f) => (
                  <tr key={f.id}>
                    <td className="py-2">{f.numeroFatura || f.id?.slice(0, 8)}</td>
                    <td className="py-2">
                      {f.plano?.nome ? `${f.plano.nome}${f.ciclo ? ` (${f.ciclo})` : ''}` : '-'}
                    </td>
                    <td className="py-2">{formatMoney(f.valor)}</td>
                    <td className="py-2">{formatDate(f.dataVencimento)}</td>
                    <td className="py-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        f.status === 'paid' ? 'bg-green-100 text-green-800' : f.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-slate-100'
                      }`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="py-2">
                      {f.asaasBankSlipUrl && (
                        <a href={f.asaasBankSlipUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline mr-2">Boleto</a>
                      )}
                      {f.asaasInvoiceUrl && (
                        <a href={f.asaasInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Fatura</a>
                      )}
                      {!f.asaasBankSlipUrl && !f.asaasInvoiceUrl && <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {modalFatura && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Nova fatura</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-slate-600 text-sm mb-1">Tipo</label>
                      <select value={formFatura.tipo} onChange={(e) => setFormFatura((f) => ({ ...f, tipo: e.target.value }))} className="w-full px-3 py-2 border rounded">
                        <option value="plano">Por plano (mensal, trimestral, semestral, anual)</option>
                        <option value="manual">Cobrança manual (valor livre)</option>
                      </select>
                    </div>
                    {formFatura.tipo === 'plano' && (
                      <>
                        <div>
                          <label className="block text-slate-600 text-sm mb-1">Plano</label>
                          <select value={formFatura.plano_id} onChange={(e) => setFormFatura((f) => ({ ...f, plano_id: e.target.value }))} className="w-full px-3 py-2 border rounded">
                            <option value="">Selecione o plano</option>
                            {planos.filter((p) => p.ativo).map((p) => (
                              <option key={p.id} value={p.id}>{p.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-slate-600 text-sm mb-1">Ciclo</label>
                          <select value={formFatura.ciclo} onChange={(e) => setFormFatura((f) => ({ ...f, ciclo: e.target.value }))} className="w-full px-3 py-2 border rounded">
                            <option value="mensal">Mensal</option>
                            <option value="trimestral">Trimestral (3 meses)</option>
                            <option value="semestral">Semestral (6 meses)</option>
                            <option value="anual">Anual (12 meses)</option>
                          </select>
                        </div>
                        {valorCalculadoFatura() != null && (
                          <p className="text-sm text-slate-700">Valor: <strong>{formatMoney(valorCalculadoFatura())}</strong></p>
                        )}
                      </>
                    )}
                    {formFatura.tipo === 'manual' && (
                      <>
                        <div>
                          <label className="block text-slate-600 text-sm mb-1">Valor (R$)</label>
                          <input type="number" step="0.01" min="0" value={formFatura.valor} onChange={(e) => setFormFatura((f) => ({ ...f, valor: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="0,00" />
                        </div>
                        <div>
                          <label className="block text-slate-600 text-sm mb-1">Descrição</label>
                          <input value={formFatura.descricao} onChange={(e) => setFormFatura((f) => ({ ...f, descricao: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="Ex: Cobrança avulsa" />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-slate-600 text-sm mb-1">Data vencimento</label>
                      <input type="date" value={formFatura.data_vencimento} onChange={(e) => setFormFatura((f) => ({ ...f, data_vencimento: e.target.value }))} className="w-full px-3 py-2 border rounded" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button type="button" onClick={() => setModalFatura(false)} className="px-4 py-2 border rounded text-sm">Cancelar</button>
                    <button type="button" onClick={salvarFatura} disabled={salvandoFatura} className="px-4 py-2 bg-slate-800 text-white rounded text-sm disabled:opacity-50">Criar fatura</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {aba === 'atividades' && (
          <div className="text-sm">
            {carregandoAtividades ? (
              <p className="text-slate-500">Carregando...</p>
            ) : atividades.length === 0 ? (
              <p className="text-slate-500">Nenhuma atividade registrada.</p>
            ) : (
              <ul className="space-y-2">
                {atividades.map((log) => (
                  <li key={log.id} className="flex flex-wrap gap-2 border-b border-slate-100 pb-2">
                    <span className="font-medium">{log.acao}</span>
                    <span className="text-slate-500">{log.superAdmin?.nome}</span>
                    <span className="text-slate-400">{formatDate(log.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {aba === 'acoes' && (
          <div className="space-y-4">
            {!acaoExtra ? (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setAcaoExtra({ tipo: 'ativar' })}
                  className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Ativar empresa
                </button>
                <button
                  type="button"
                  onClick={() => setAcaoExtra({ tipo: 'suspender' })}
                  className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Suspender empresa
                </button>
                <button
                  type="button"
                  onClick={() => setAcaoExtra({ tipo: 'alterarPlano' })}
                  className="px-3 py-1.5 bg-slate-600 text-white rounded text-sm hover:bg-slate-700"
                >
                  Alterar plano
                </button>
                <button
                  type="button"
                  onClick={() => setAcaoExtra({ tipo: 'estenderTrial' })}
                  className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700"
                >
                  Estender trial
                </button>
                <button
                  type="button"
                  onClick={() => setAcaoExtra({ tipo: 'resetarSenha' })}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-50"
                >
                  Resetar senha do admin
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {acaoExtra.tipo === 'alterarPlano' && (
                  <>
                    <select
                      className="px-3 py-2 border border-slate-300 rounded"
                      value={acaoExtra.valor || ''}
                      onChange={(ev) => setAcaoExtra((a) => ({ ...a, valor: ev.target.value }))}
                    >
                      <option value="">Selecione o plano</option>
                      {planos.filter((p) => p.ativo).map((p) => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                  </>
                )}
                {acaoExtra.tipo === 'estenderTrial' && (
                  <input
                    type="number"
                    min="1"
                    placeholder="Dias"
                    className="px-3 py-2 border border-slate-300 rounded w-24"
                    value={acaoExtra.valor ?? ''}
                    onChange={(ev) => setAcaoExtra((a) => ({ ...a, valor: ev.target.value }))}
                  />
                )}
                {acaoExtra.tipo === 'resetarSenha' && (
                  <input
                    type="password"
                    placeholder="Nova senha"
                    className="px-3 py-2 border border-slate-300 rounded"
                    value={acaoExtra.valor ?? ''}
                    onChange={(ev) => setAcaoExtra((a) => ({ ...a, valor: ev.target.value }))}
                  />
                )}
                <button
                  type="button"
                  onClick={executarAcao}
                  className="px-3 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700"
                >
                  Executar
                </button>
                <button
                  type="button"
                  onClick={() => setAcaoExtra(null)}
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
